import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { CareerPathEvaluationState, CareerPathEvaluation, CareerPathDto, Currency } from '../../../types'
import { Popover, Col } from 'antd'
import PerIcon from '../Icon'
import PerFlexRow from '../FlexRow'
import PerText from '../Text'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'

type ProfileLabelListComponentProps = {
  careerPath: CareerPathEvaluation
  bonusReference: CareerPathDto
  salaryReference: CareerPathDto
  currency: Currency
  hasLabel?: boolean
}

export const CareerPathPopover: FC<ProfileLabelListComponentProps> = ({
  careerPath,
  bonusReference,
  salaryReference,
  hasLabel = false,
  currency,
}) => {
  const [t] = useTranslation()
  const { formatCurrencySymbol } = useAmountAndCurrency()

  const color = (state: CareerPathEvaluationState) => {
    if (state === CareerPathEvaluationState.Inline) {
      return 'success'
    } else if (state === CareerPathEvaluationState.OutOfLine) {
      return 'error'
    } else {
      return 'secondary'
    }
  }

  const generateTooltip = (type, state) => {
    const text = t('global.careerPath.description.text')
    if (type !== 'summary') {
      if (state === undefined) {
        return `${text} ${t('global.careerPath.description.notFound')}`
      } else {
        return `${t(`global.careerPath.type.${type}`)} ${text} ${t(`global.careerPath.description.${state}`)}`
      }
    } else {
      return `${t('global.careerPath.label')} ${t(`global.careerPath.description.${state}`)}`
    }
  }

  const renderIcon = (iconType, tooltip?: string) => <PerIcon name="circle_fill" color={color(iconType)} size={10} tooltip={tooltip} />

  const renderRow = (
    careerInfo: keyof CareerPathEvaluation,
    careerValue: CareerPathEvaluationState,
    reference: CareerPathDto,
    currentValue: number,
  ) => {
    const formattedLimits = `${reference.min / 1000} - ${reference.max ? reference.max / 1000 : '&infin;'}`
    return (
      <>
        <PerFlexRow justify="space-between" mt="xxxxs">
          <Col span={6}>
            <PerText align="left">{t(`global.careerPath.type.${careerInfo}`)} </PerText>
          </Col>
          <Col span={2}>
            <PerText align="center">{renderIcon(careerValue, generateTooltip(careerInfo, careerValue))}</PerText>
          </Col>
          <Col span={8}>
            <PerText align="center" ml="s">
              {currentValue}
            </PerText>
          </Col>
          <Col span={8}>
            <PerText align="center" ml="s">
              {formattedLimits}
            </PerText>
          </Col>
        </PerFlexRow>
      </>
    )
  }
  const summaryReference = {
    min: salaryReference.min + bonusReference.min,
    max: salaryReference.max && bonusReference.max ? salaryReference.max + bonusReference.max : null,
  } as CareerPathDto
  const roundedSalaryAndBonus = Math.round((Number(careerPath.salary + careerPath.targetBonus) + Number.EPSILON) * 100) / 100
  return (
    <Popover
      placement="right"
      overlayStyle={{ width: '375px' }}
      title={t('appraisalMeeting.meetingStepper.dropdown.title')}
      content={
        <>
          <PerFlexRow>
            <Col span={8} />
            <Col span={8}>
              <PerText color="secondary" align="center">
                {t('global.careerPath.popover.current', { currency: formatCurrencySymbol(currency) })}
              </PerText>
            </Col>
            <Col span={8}>
              <PerText color="secondary" align="center">
                {t('global.careerPath.popover.reference', { currency: formatCurrencySymbol(currency) })}
              </PerText>
            </Col>
          </PerFlexRow>
          {renderRow('salaryEvaluation', careerPath.salaryEvaluation, salaryReference, careerPath.salary)}
          {renderRow('bonusEvaluation', careerPath.bonusEvaluation, bonusReference, careerPath.targetBonus)}
          {renderRow('summary', careerPath.summary, summaryReference, roundedSalaryAndBonus)}
        </>
      }
    >
      {hasLabel && (
        <Col span={10}>
          <PerText color="secondary" mr="xs">
            {t('global.careerPath.label')}:
          </PerText>
        </Col>
      )}
      <div>{!!careerPath && renderIcon(careerPath.summary)}</div>
    </Popover>
  )
}
